/**
 * Bundle-coordinate derivation for app-health error events (RFC-0974 follow-up,
 * spec §4 / §5.2).
 *
 * Emits WHERE in our own build an error was thrown — `<chunk>:<line>:<col>`,
 * e.g. `index-CjKl9_Qd.js:8421:19` — and never the message, the stack text, or
 * any user-facing URL. Paired with `error_name` this is a stable grouping key:
 * "one site is 60% of events" is answerable in the warehouse, and the deployed
 * source map resolves the coordinate back to a real file:line offline.
 *
 * PII stance (§4): the ONLY thing forwarded is a Vite build-output filename
 * plus two integers. A frame is required to be same-origin AND under `/assets/`
 * (our `build.rollupOptions` output dir) before its basename is used, and that
 * basename must additionally match a strict `[A-Za-z0-9._-]` charset — so a
 * route path, a query string, a signed asset URL, or an email can never reach
 * the field. Everything else collapses to a sentinel:
 *
 *   - `cross-origin` — a third-party script or a browser extension. Lets the
 *     dashboard exclude noise that is not our code rather than counting it.
 *   - `inline`       — same-origin but not a build asset (inline `<script>`,
 *     the document itself).
 *   - `unknown`      — no usable frame (rejections with no stack, opaque
 *     cross-origin "Script error." events with an empty filename).
 */

export const ERROR_SITE_UNKNOWN = 'unknown'
export const ERROR_SITE_CROSS_ORIGIN = 'cross-origin'
export const ERROR_SITE_INLINE = 'inline'

/** Vite's `build.assetsDir` — the only path prefix whose basename we forward. */
const ASSETS_PREFIX = '/assets/'

/** Build-output basenames only: `index-CjKl9_Qd.js`. No `@`, no spaces, no `/`. */
const SAFE_BASENAME = /^[A-Za-z0-9._-]+$/

/** Stack FRAME forms only — deliberately not "any URL in the stack text". The
 *  message line can itself contain a URL with a `:line:col` tail (`Failed to
 *  load https://…/assets/x.js:1:1`), and it precedes the frames, so a loose
 *  search would attribute the error to whatever the message mentions.
 *    V8:              `    at fn (https://h/assets/a.js:1:2)` / `    at https://…:1:2`
 *    SpiderMonkey/JSC: `fn@https://h/assets/a.js:1:2`
 *  Both are anchored to the whole line, and the `@` form requires the scheme to
 *  follow immediately, so an email in a message cannot match. */
const V8_FRAME = /^\s*at\s+(?:[^()]*\()?(https?:\/\/[^\s()]+?):(\d+):(\d+)\)?\s*$/
const AT_SIGN_FRAME = /^.*?@(https?:\/\/[^\s()]+?):(\d+):(\d+)\s*$/

/** Belt-and-braces bound; a conforming value is well under this. */
const MAX_LEN = 120

function isInt(n: number | undefined, min: number): boolean {
  return typeof n === 'number' && Number.isFinite(n) && Number.isInteger(n) && n >= min
}

/** Source lines are 1-based: `ErrorEvent.lineno` is 0 when the browser has no
 *  location for the throw, and emitting `index.js:0:0` would be a fabricated
 *  coordinate that groups unrelated errors together. Columns are 0-based and a
 *  genuine 0 is fine. */
function hasValidPosition(line: number | undefined, col: number | undefined): boolean {
  return isInt(line, 1) && isInt(col, 0)
}

/**
 * Reduce one script URL + position to a site token. Exported for direct unit
 * testing of the origin/charset gates.
 */
export function siteFromFrame(
  url: string | undefined,
  line: number | undefined,
  col: number | undefined,
): string {
  if (typeof window === 'undefined') return ERROR_SITE_UNKNOWN
  if (!url) return ERROR_SITE_UNKNOWN
  let parsed: URL
  try {
    // Relative bases resolve against the document; `URL` drops nothing we need.
    parsed = new URL(url, window.location.href)
  } catch {
    return ERROR_SITE_UNKNOWN
  }
  if (parsed.origin !== window.location.origin) return ERROR_SITE_CROSS_ORIGIN
  // `.pathname` has already discarded `?query` and `#fragment`.
  if (!parsed.pathname.startsWith(ASSETS_PREFIX)) return ERROR_SITE_INLINE
  const basename = parsed.pathname.slice(parsed.pathname.lastIndexOf('/') + 1)
  if (!basename || !SAFE_BASENAME.test(basename)) return ERROR_SITE_INLINE
  // Position is optional: a chunk name alone is still a usable grouping key.
  if (!hasValidPosition(line, col)) return basename.slice(0, MAX_LEN)
  return `${basename}:${line}:${col}`.slice(0, MAX_LEN)
}

/**
 * Site for a window `error` event. Uses the structured `filename`/`lineno`/
 * `colno` fields rather than parsing `event.error.stack` — they are already
 * split, and they survive the cross-origin case where `error` itself is null.
 */
export function siteFromErrorEvent(event: ErrorEvent): string {
  try {
    return siteFromFrame(event.filename, event.lineno, event.colno)
  } catch {
    return ERROR_SITE_UNKNOWN
  }
}

/**
 * Site for an `unhandledrejection`. There is no structured position on the
 * event, so read the TOP frame of `reason.stack` — the frame URL and its
 * line:col only, never the surrounding stack text. A reason with no stack
 * (string rejections, plain objects) yields `unknown`.
 */
export function siteFromRejectionReason(reason: { stack?: string | null } | null | undefined): string {
  try {
    if (!reason || typeof reason !== 'object') return ERROR_SITE_UNKNOWN
    const stack = reason.stack
    if (typeof stack !== 'string') return ERROR_SITE_UNKNOWN
    // Line by line, so only a real frame can match — never the message line.
    for (const line of stack.split('\n')) {
      const match = V8_FRAME.exec(line) ?? AT_SIGN_FRAME.exec(line)
      if (match) return siteFromFrame(match[1], Number(match[2]), Number(match[3]))
    }
    return ERROR_SITE_UNKNOWN
  } catch {
    return ERROR_SITE_UNKNOWN
  }
}

/**
 * Site for a caught `Error` (React error boundaries), from its own stack.
 */
export function siteFromError(error: Error | null | undefined): string {
  return siteFromRejectionReason(error)
}
